import { PropsWithChildren, useMemo } from 'react';
import { useWindowDimensions } from 'react-native';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { isWeb } from '@tamagui/core';
import { Dialog as TamaguiDialog, DialogProps as TamaguiDialogProps } from '@tamagui/dialog';

import { AnimationName } from '../../animationTypes';
import { getOpacityTokenValue, getPercentageTokenValue } from '../../helpers/tokens';
import { Box, BoxProps } from '../Box';
import { CloseButton } from '../Button';
import { BackgroundBlur } from './BackgroundBlur';

const { dialog: dialogTokens } = tokens;

export type DialogProps = Pick<TamaguiDialogProps, 'onOpenChange'> &
  Pick<BoxProps, 'analyticsName' | 'trackImpression'> & {
    /**
     * Provide custom animation to dialog. Defaults to "accelerateDecelerateFastest"
     */
    animation?: AnimationName;
    /**
     * State representing whether the dialog is open or not (required)
     */
    isOpen: boolean;
    /**
     * Callback to change the isOpen state (required)
     */
    setIsOpen: (isOpen: boolean) => void;
    /**
     * Whether the dialog can be dismissed by clicking the close button or the
     * overlay or dragging the handle on touch devices. Defaults to true. If
     * false, the close button and the handle will be hidden.
     */
    dismissable?: boolean;
    /**
     * Whether the overlay should be dark or light. Defaults to "dark".
     */
    overlayColor?: 'dark' | 'light' | 'transparent';
  };

/**
 * Dialog is used to display attention-grabbing content on top of the rest of
 * the page. The component is responsive: on small screens, it displays as a
 * sheet that can be dragged up and down, while on larger screens, it displays
 * as a modal that can be dismissed by clicking the overlay or the close button.
 * If the component should not be dismissable, you can pass
 * `dismissable={false}`.
 *
 * Dialog is a controlled component, so it's required that you keep track of the
 * state of the component externally and pass both the `isOpen` and `setIsOpen`
 * props. (Otherwise, the dialog will never open or close.)
 *
 * `Dialog.Content` is a required child of Dialog. It renders the content you
 * want to display in a scrollable container. You must pass `title` and
 * `description` props to `Dialog.Content` for a11y. If you need to visually
 * hide either the title or description, you can pass `hideTitle` or
 * `hideDescription`, respectively, and pass custom content as a child of
 * `Dialog.Content`. If, on the other hand, you want to display the title or
 * description but customize how it gets rendered, you can pass `titleProps` or
 * `descriptionProps`, respectively. Note that both title and description have
 * default `$largerThanSM` props, so if you want to customize how they render on
 * larger screens, you'll need to pass `$largerThanSM` as part of `titleProps`
 * or `descriptionProps`.
 *
 * Dialog also takes an optional child for displaying actions: `Dialog.Actions`.
 * The action component should go after `Dialog.Content` so it can be displayed
 * outside of the scrollable container.
 *
 * The complete anatomy of Dialog looks something like this (depending on which
 * actions you need, if any):
 *
 * ```tsx
 * const MyComponent = () => {
 *   const [isOpen, setIsOpen] = useState(false);
 *   return (
 *     <Dialog isOpen={isOpen} setIsOpen={setIsOpen}>
 *       <Dialog.Content title="Dialog Title" description="Dialog Description" />
 *       <Dialog.Actions
 *         checkboxProps={{ label: 'Checkbox Label', value: true, analyticsName: '' }}
 *         buttonOneProps={{ variant: 'secondary', analyticsName: '', label: 'Save', onPress: () => {} }}
 *         buttonTwoProps={{ variant: 'primary', analyticsName: '', label: 'Cancel', onPress: () => {} }}
 *       />
 *     </Dialog>
 *   );
 * };
 * ```
 *
 * If you pass a non-empty `analyticsName` and `trackImpression={true}` to the
 * Dialog component, the component will track view events ("boxViewEvent").
 * Analytics props can also be passed to the buttons inside the action
 * components via the respective button props.
 */
export const Dialog = ({
  isOpen,
  animation = 'accelerateDecelerateFastest',
  setIsOpen,
  overlayColor = 'dark',
  analyticsName,
  trackImpression,
  dismissable = true,
  children,
}: PropsWithChildren<DialogProps>) => {
  const overlayProps =
    overlayColor === 'dark'
      ? { backgroundColor: dialogTokens.color.scrim }
      : overlayColor === 'transparent'
      ? { backgroundColor: undefined }
      : {
          backgroundColor: dialogTokens.color.scrimInverse,
          children: (
            <BackgroundBlur
              blurRadius={dialogTokens.backgroundBlur.default}
              style={{ position: 'absolute', top: 0, bottom: 0, right: 0, left: 0 }}
            />
          ),
        };

  const maxHeightTokenValue = getPercentageTokenValue(dialogTokens.percentage.maxHeight);
  const minHeightTokenValue = getPercentageTokenValue(dialogTokens.percentage.minHeightWidth);

  const dimensions = useWindowDimensions();

  const maxSheetHeight = useMemo((): BoxProps['maxHeight'] => {
    const percent = parseInt(maxHeightTokenValue, 10);
    if (isWeb) {
      return `${percent}dvh`;
    }
    return 0.01 * percent * dimensions.height;
  }, [dimensions.height, maxHeightTokenValue]);

  const minSheetHeight = useMemo((): BoxProps['minHeight'] => {
    const percent = parseInt(minHeightTokenValue, 10);
    if (isWeb) {
      return `${percent}dvh`;
    }
    return 0.01 * percent * dimensions.height;
  }, [dimensions.height, minHeightTokenValue]);

  return (
    <TamaguiDialog
      modal
      open={isOpen}
      // This seems to be how clicking the overlay closes the dialog. If
      // dismissable is false, then clicking the overlay won't close the dialog.
      onOpenChange={dismissable ? setIsOpen : undefined}
    >
      <TamaguiDialog.Portal>
        <TamaguiDialog.Overlay
          key="overlay"
          animation={animation}
          animateOnly={['opacity']}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
          {...overlayProps}
        />
        <TamaguiDialog.Content
          key="content"
          animation={animation}
          animateOnly={['opacity', 'transform']}
          enterStyle={{ opacity: 0, y: 20 }}
          exitStyle={{ opacity: 0, y: 20 }}
          overflow="hidden"
          padding={0}
          borderWidth={0}
          backgroundColor={dialogTokens.color.background.default}
          borderRadius={dialogTokens.cornerRadius.desktop.default}
          minWidth={getPercentageTokenValue(dialogTokens.percentage.minHeightWidth)}
          minHeight={minHeightTokenValue}
          maxHeight={maxHeightTokenValue}
          $largerThanSM={{
            maxWidth: getPercentageTokenValue(dialogTokens.percentage.maxWidth),
          }}
        >
          <Box
            analyticsName={analyticsName}
            trackImpression={trackImpression}
            flexGrow={1}
            flexShrink={1}
            position="relative"
          >
            {dismissable && (
              <TamaguiDialog.Close asChild>
                <CloseButton
                  size="large"
                  analyticsName={`${analyticsName}-close`}
                  position="absolute"
                  zIndex={1}
                  top={0}
                  right={0}
                  margin={dialogTokens.padding.actions.closeIconButton.desktop}
                />
              </TamaguiDialog.Close>
            )}

            {children}
          </Box>
        </TamaguiDialog.Content>
      </TamaguiDialog.Portal>

      <TamaguiDialog.Adapt when="smallerThanMD">
        <TamaguiDialog.Sheet
          modal
          dismissOnSnapToBottom={dismissable}
          moveOnKeyboardChange
          dismissOnOverlayPress={dismissable}
          disableDrag={!dismissable}
          animation="accelerateDecelerateFastest"
          snapPointsMode="fit"
        >
          <TamaguiDialog.Sheet.Overlay
            key="overlay"
            animation="accelerateDecelerateFastest"
            animateOnly={['opacity']}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
            {...overlayProps}
          />
          {dismissable && (
            <TamaguiDialog.Sheet.Handle
              borderRadius={dialogTokens.cornerRadius.mobile.handle}
              height={dialogTokens.mobile.handle.height}
              width={dialogTokens.mobile.handle.width}
              alignSelf="center"
              marginLeft={0}
              marginRight={0}
              marginBottom={dialogTokens.margin.mobile.handle.bottom}
              backgroundColor={
                overlayColor === 'dark'
                  ? dialogTokens.color.background.mobile.handle
                  : dialogTokens.color.background.mobile.handleInverse
              }
              opacity={getOpacityTokenValue(dialogTokens.opacity.mobileHandle)}
            />
          )}
          <TamaguiDialog.Sheet.Frame
            backgroundColor={dialogTokens.color.background.default}
            borderTopLeftRadius={dialogTokens.cornerRadius.mobile.default}
            borderTopRightRadius={dialogTokens.cornerRadius.mobile.default}
            maxHeight={maxSheetHeight}
            minHeight={minSheetHeight}
          >
            <TamaguiDialog.Adapt.Contents />
          </TamaguiDialog.Sheet.Frame>
        </TamaguiDialog.Sheet>
      </TamaguiDialog.Adapt>
    </TamaguiDialog>
  );
};
